import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPlayState, submitAnswer, sendPresence } from '../services/api';
import { ClueScene } from '../components/hunt/ClueScene';
import { AnswerInput } from '../components/hunt/AnswerInput';
import { ProgressMap } from '../components/hunt/ProgressMap';
import { CompletionScreen } from '../components/hunt/CompletionScreen';
import { Crane } from '../components/mascot/Crane';
import { SpeechBubble } from '../components/mascot/SpeechBubble';
import { ConfettiBurst } from '../components/common/ConfettiBurst';

export function HuntPage() {
  const { huntId } = useParams<{ huntId: string }>();
  const queryClient = useQueryClient();
  const [craneMessage, setCraneMessage] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const [isCelebrating, setIsCelebrating] = useState(false);
  const [wasWrong, setWasWrong] = useState(false);

  const { data: playState, isLoading, error } = useQuery({
    queryKey: ['playState', huntId],
    queryFn: () => getPlayState(huntId!),
    enabled: !!huntId,
  });

  const submitMutation = useMutation({
    mutationFn: (answer: string) => submitAnswer(huntId!, answer),
    onSuccess: (result) => {
      if (result.correct) {
        setWasWrong(false);
        setCraneMessage(
          result.fragment
            ? `You pulled a word loose: "${result.fragment}". Keep hold of it.`
            : 'Excellent! The map unfolds a little more...'
        );
        setIsCelebrating(true);
        setTimeout(() => setIsCelebrating(false), 2600);
        queryClient.invalidateQueries({ queryKey: ['playState', huntId] });
      } else {
        setWasWrong(true);
        setTimeout(() => setWasWrong(false), 600);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);

        const messages = [
          'Not quite. Try again.',
          'Hmm, that doesn\'t fit. Keep thinking.',
          'The creases don\'t align. Another guess?',
          'Close, but the paper resists.',
        ] as const;
        setCraneMessage(messages[Math.floor(Math.random() * messages.length)] ?? 'Try again.');

        if (result.cooldownUntil) {
          const cooldownMs = new Date(result.cooldownUntil).getTime() - Date.now();
          setCraneMessage(`Slow down. Rest for ${Math.ceil(cooldownMs / 1000)} seconds.`);
        }
      }
    },
  });

  // Presence heartbeat
  useEffect(() => {
    if (!huntId) return;

    sendPresence(huntId);
    const interval = setInterval(() => sendPresence(huntId), 30000);
    return () => clearInterval(interval);
  }, [huntId]);

  // Clear crane message after delay
  useEffect(() => {
    if (craneMessage) {
      const timer = setTimeout(() => setCraneMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [craneMessage]);

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-pulse text-text-light">Loading hunt...</div>
      </div>
    );
  }

  if (error || !playState) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-red-600">Failed to load hunt</div>
      </div>
    );
  }

  // Completed
  if (playState.progress.completedAt) {
    return (
      <CompletionScreen
        huntId={huntId!}
        fragments={playState.fragments}
      />
    );
  }

  const { currentClue, progress } = playState;
  const cooldownActive = progress.cooldownUntil
    ? new Date(progress.cooldownUntil) > new Date()
    : false;

  const cranePose = isCelebrating
    ? 'celebrating'
    : wasWrong
      ? 'sympathetic'
      : submitMutation.isPending
        ? 'thinking'
        : 'pointing';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {isCelebrating && <ConfettiBurst />}

      {/* Progress Bar */}
      <div className="mb-8">
        <ProgressMap
          currentClue={progress.currentClueIndex}
          totalClues={progress.totalClues}
          solvedCount={progress.solvedCount}
        />
      </div>

      {/* Mascot */}
      <div className="flex justify-center mb-6 relative">
        <Crane pose={cranePose} />
        {craneMessage && (
          <SpeechBubble message={craneMessage} />
        )}
      </div>

      {/* Clue Scene - keyed by clue index so it re-animates on each new clue */}
      {currentClue && (
        <div
          key={currentClue.clueIndex}
          className={`card mb-6 animate-fade-in-up ${isShaking ? 'animate-shake' : ''}`}
        >
          <div className="text-sm text-text-light mb-2">
            Clue {currentClue.clueIndex} of {progress.totalClues}
          </div>
          <ClueScene clue={currentClue} />
        </div>
      )}

      {/* Answer Input */}
      <AnswerInput
        onSubmit={(answer) => submitMutation.mutate(answer)}
        isSubmitting={submitMutation.isPending}
        disabled={cooldownActive}
        cooldownUntil={progress.cooldownUntil}
      />

      {/* Collected fragments - the words that assemble into the finale */}
      {playState.fragments && Object.keys(playState.fragments).length > 0 && (
        <div className="mt-6 p-4 bg-white/70 border border-gray-200 rounded-lg">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-light mb-2">
            Fragments collected
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(playState.fragments)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([clueIdx, word]) => (
                <span
                  key={clueIdx}
                  className="px-3 py-1 rounded-full bg-secondary/10 text-secondary font-semibold text-sm animate-pop-in"
                  title={`From clue ${clueIdx}`}
                >
                  {word}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* Hint */}
      {currentClue?.hintReleased && currentClue.hintText && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <span className="font-semibold text-yellow-800">Hint:</span>{' '}
          <span className="text-yellow-700">{currentClue.hintText}</span>
        </div>
      )}
    </div>
  );
}
